import React, { useRef } from 'react';
import { Breadcrumbs } from '../../../components/uikit/Breadcrumbs.tsx';
import { useScopedTranslation } from '../../../application/localization/useScopedTranslation.ts';
import styles from './ServicePresentation.module.scss';
import { Input } from '../../../components/uikit/inputs/Input.tsx';
import { useAdvancedForm } from '../../../helpers/form/useAdvancedForm.ts';
import { useWatch } from 'react-hook-form';
import { Button } from '../../../components/uikit/buttons/Button.tsx';
import clsx from 'clsx';
import {
  BooleanParam,
  StringParam,
  useQueryParams,
  withDefault,
} from 'react-router-url-params';
import {
  pagingSortingQueryParams,
  pagingSortingToBackendRequest,
} from '../../../helpers/pagination-helper.ts';
import { Loading } from '../../../components/uikit/suspense/Loading.tsx';
import { IconButton } from '../../../components/uikit/IconButton.tsx';
import Cross from 'assets/icons/close-button-small.svg?react';
import ArrowRight from 'assets/icons/arrow-long-right.svg?react';
import { Trans, useTranslation, UseTranslationResponse } from 'react-i18next';
import {
  DataGridRef,
  IColumnProps,
  Pager,
  Scrolling,
} from 'devextreme-react/data-grid';
import formStyles from '../../../components/form/form.module.scss';
import {
  DXG,
  useDataGrid,
} from '../../../components/devexpress/Grid/dx-grid.ts';
import gridStyles from '../../../components/devexpress/Grid/grid.module.scss';
import {
  onContextMenuPreparing,
  TableHeaderCell,
} from '../../../helpers/tableHeaderCell/TableHeaderCell.tsx';
import { headerSortClick } from '../../../helpers/sort-helper.ts';
import {
  abasUnavailableUrn,
  ErrObj,
  formatDate,
} from '../../../helpers/abas-helper.ts';
import { NegativeActionText } from '../../../components/uikit/NegativeActionText.tsx';
import { ServicePresentationQuery } from 'services/api/api-client.ts';
import { AppPagination } from '../../../components/uikit/pagination/AppPagination.tsx';
import { TablePlaceholder } from '../../../components/uikit/table/TablePlaceholder.tsx';
import { downloadOrOpenBlobFile } from '../../../helpers/download-helper.ts';
import { LottieContainer } from '../../../components/animations/LottieContainer.tsx';
import animationData from '../../../assets/animations/loader_small.json';
import { ActionText } from '../../../components/uikit/ActionText.tsx';
import { useProcessLinks } from '../../../helpers/useProcessLinks.ts';
import { Tooltip } from '../../../components/uikit/Tooltip.tsx';
import { useAnalytics } from 'helpers/analytics.ts';
import { TrackEventParams } from '@jonkoops/matomo-tracker-react/lib/types';
import { Tip } from '../../../components/uikit/Tip.tsx';
import { AbasPlaceholder } from '../../../components/uikit/AbasPlaceholder.tsx';

export const ServicePresentationCategory = 'Service presentation';

type Form = {
  name: string;
};
const pageSizes = [10, 20, 50];

const mail = 'supportHC@loewensteinmedical.com';
const none = 'none';

export const ServicePresentation = () => {
  const i18n = useScopedTranslation('Page.ServicePresentation');
  const i18nCommon = useTranslation();

  const { trackEvent } = useAnalytics();

  const {
    addProcessingLink,
    removeProcessingLink,
    processingLinks,
    showTooltip,
    isTooltipVisible,
    closeTooltip,
  } = useProcessLinks();

  const [params, setParams] = useQueryParams({
    name: StringParam,
    ...pagingSortingQueryParams(pageSizes[0]),
    sortBy: withDefault(StringParam, 'validFrom'),
    desc: withDefault(BooleanParam, true),
  });

  const form = useAdvancedForm<Form>(
    async (data) => {
      setParams({ ...params, ...data, page: 1 });
    },
    {
      defaultValues: {
        name: params.name ?? '',
      },
    },
  );

  const servicePresentationData = ServicePresentationQuery.useGetQuery(
    {
      name: params.name ?? '',
      ...pagingSortingToBackendRequest(params),
    },
    {
      throwOnError: false,
      staleTime: 30 * 1000,
      placeholderData: (previousData) => previousData,
    },
  );

  const [name] = useWatch({
    control: form.control,
    name: ['name'],
  });

  const columnsConfig = getColumnsConfig(
    i18n,
    i18nCommon,
    addProcessingLink,
    removeProcessingLink,
    processingLinks,
    showTooltip,
    trackEvent,
  );
  const dataGridRef = useRef<DataGridRef<any, number>>(null);
  useDataGrid();

  const changePagination = (pagination: {
    page?: number;
    perPage?: number;
  }) => {
    setParams((prevParams) => ({
      ...prevParams,
      ...(pagination.page !== undefined && { page: pagination.page }),
      ...(pagination.perPage !== undefined && { perPage: pagination.perPage }),
    }));
  };

  const numberOfRows =
    servicePresentationData.data?.serviceDocuments.length ?? 0;

  const paginationProps = {
    page: params.page,
    perPage: params.perPage,
    totalCount: servicePresentationData.data?.countOfRecords ?? 0,
    pageSizes: pageSizes,
    numberOfRows,
    changePagination: changePagination,
  };

  const changeSorting = (sortBy: string) => {
    setParams((prevParams) => ({
      ...prevParams,
      sortBy,
      desc: prevParams.sortBy !== sortBy ? false : !prevParams.desc,
      page: 1,
    }));
  };

  const resetFilters = () => {
    form.reset({
      name: '',
    });

    void form.handleSubmitDefault();
  };

  const isResetButtonShown = !!params.name;

  const errObj = servicePresentationData?.error as ErrObj;
  const isExternalServiceUnavailable =
    errObj?.response?.type === abasUnavailableUrn;

  const isPlaceholderVisible =
    !numberOfRows &&
    !servicePresentationData.isLoading &&
    !servicePresentationData.isPlaceholderData &&
    !isExternalServiceUnavailable;

  return (
    <div data-test-id={'service-presentation-container'}>
      <Breadcrumbs />
      <Loading
        loading={
          servicePresentationData.isLoading ||
          servicePresentationData.isPlaceholderData
        }
      >
        <form onSubmit={form.handleSubmitDefault}>
          <div className={styles.filterRowContainer}>
            <Input
              testId={'name-input'}
              {...form.register('name')}
              leftHelperText={i18n.t('name')}
              className={styles.width352}
              endAdornment={
                <div>
                  {name && (
                    <IconButton
                      type={'destroy'}
                      onClick={() => form.setValue('name', '')}
                    >
                      <Cross />
                    </IconButton>
                  )}
                </div>
              }
              endAdornmentClassname={styles.endAdornmentClassname}
            />
            <Button
              testId={'filters-apply-button'}
              type={'submit'}
              className={clsx(styles.actionButton)}
              title={i18n.t('apply')}
            />
            {isResetButtonShown && (
              <NegativeActionText
                testId={'filters-reset-button'}
                text={i18n.t('reset')}
                onClickHandler={resetFilters}
              />
            )}
          </div>
        </form>
        {!isExternalServiceUnavailable && (
          <DXG.DataGrid
            ref={dataGridRef}
            dataSource={servicePresentationData.data?.serviceDocuments}
            className={clsx(
              gridStyles.editorOutline,
              formStyles.table,
              styles.fix,
            )}
            sorting={{ mode: 'none' }}
            paging={{ pageSize: 150 }}
            noDataText={''}
            onContextMenuPreparing={onContextMenuPreparing}
            onCellClick={(event) => headerSortClick(event, changeSorting)}
          >
            <Scrolling
              columnRenderingMode={'virtual'}
              showScrollbar={'always'}
            />
            {columnsConfig.map((x, index) => {
              return (
                <DXG.Column
                  cssClass={clsx(x.cssClass, formStyles.text)}
                  key={`${x.dataField}.${index}`}
                  showEditorAlways={x.allowEditing}
                  headerCellRender={(cellInfo) => (
                    <TableHeaderCell
                      dataField={cellInfo.column.dataField}
                      caption={cellInfo.column.caption}
                      sortBy={params.sortBy}
                      desc={params.desc}
                    />
                  )}
                  {...x}
                />
              );
            })}
            <Pager visible={false} />
          </DXG.DataGrid>
        )}
        {isExternalServiceUnavailable && (
          <AbasPlaceholder containerClassname={styles.marginTop152} />
        )}
        {isPlaceholderVisible && <TablePlaceholder />}
        {!!numberOfRows && <AppPagination {...paginationProps} />}
        {isTooltipVisible && (
          <Tooltip
            isError={true}
            closeTooltip={closeTooltip}
            bodyContent={
              <Trans
                i18nKey={'fileNotFound'}
                components={{
                  supportLink: <a href={`mailto:${mail}`} />,
                }}
              />
            }
            className={styles.tooltip}
            showCancelButton={true}
            iconClassName={styles.tooltipIcon}
          />
        )}
      </Loading>
    </div>
  );
};

const getColumnsConfig = (
  i18n: UseTranslationResponse<'translation', 'Page.ServicePresentation'>,
  i18nCommon: UseTranslationResponse<'translation', undefined>,
  addProcessingLink: (link: string) => void,
  removeProcessingLink: (link: string) => void,
  processingLinks: Set<string>,
  showTooltip: () => void,
  trackEvent: (params: TrackEventParams) => void | undefined,
): (IColumnProps & { disableExport?: boolean })[] => [
  {
    dataField: 'name',
    allowEditing: false,
    allowSorting: true,
    caption: i18n.t('name'),
    cssClass: clsx(formStyles.textCellPadding, formStyles.tableHeaderPointer),
    minWidth: 594,
  },
  {
    dataField: 'validFrom',
    allowEditing: false,
    allowSorting: true,
    caption: i18n.t('valid_from'),
    cssClass: clsx(formStyles.textCellPadding, formStyles.tableHeaderPointer),
    minWidth: 200,
    cellRender: (cell) => formatDate(cell.value),
  },
  {
    dataField: 'link',
    allowEditing: false,
    allowSorting: false,
    caption: '',
    cssClass: clsx(formStyles.textCellPadding, styles.buttonPadding),
    width: 136,
    cellRender: (cell) => {
      const filePath = cell.value;
      const extension = filePath.slice(filePath.lastIndexOf('.') + 1);

      const fileName = cell.data.name;

      const link = `download-${cell.value}`;
      const isLoading = processingLinks.has(link);

      if (cell.value === none) {
        return (
          <Tip
            tooltipText={i18nCommon.t('link_unavailable_msg')}
            textBody={
              <div
                data-test-id={'disabled-download-button'}
                className={styles.disabledButton}
              >
                {i18n.t('download')}
              </div>
            }
            className={styles.tip}
            tooltipClassName={styles.margins}
          />
        );
      }

      return (
        <>
          {isLoading ? (
            <LottieContainer
              animationData={animationData}
              segments={isLoading ? [1, 180] : [180, 270]}
              loop={isLoading}
              width={28}
              height={28}
            />
          ) : (
            <div>
              <Button
                testId={'download-button'}
                className={clsx(styles.actionButton)}
                title={i18n.t('download')}
                onClick={() => {
                  trackEvent({
                    category: ServicePresentationCategory,
                    action: 'Download file',
                    name: link,
                  });
                  addProcessingLink(link);
                  downloadOrOpenBlobFile(filePath, fileName)
                    .catch(showTooltip)
                    .finally(() => removeProcessingLink(link));
                }}
              />
              <div className={styles.caption}>{extension}</div>
            </div>
          )}
        </>
      );
    },
  },
  {
    dataField: '',
    allowEditing: false,
    allowSorting: false,
    caption: '',
    cssClass: clsx(formStyles.textCellPadding, styles.buttonPadding),
    width: 76,
    cellRender: (cell) => {
      const link = `open-${cell.value}`;
      const isLoading = processingLinks.has(link);

      if (cell.value === none) {
        return (
          <Tip
            tooltipText={i18nCommon.t('link_unavailable_msg')}
            textBody={
              <div
                data-test-id={'disabled-open-button'}
                className={styles.disabledButton}
              >
                {i18n.t('open')}
              </div>
            }
            className={styles.tip}
            tooltipClassName={styles.margins}
          />
        );
      }

      return (
        <>
          {isLoading ? (
            <LottieContainer
              animationData={animationData}
              segments={isLoading ? [1, 180] : [180, 270]}
              loop={isLoading}
              width={28}
              height={28}
            />
          ) : (
            <div
              data-test-id={'open-button'}
              className={styles.openButton}
              onClick={() => {
                trackEvent({
                  category: ServicePresentationCategory,
                  action: 'Open file',
                  name: link,
                });
                addProcessingLink(link);
                downloadOrOpenBlobFile(cell.value, '', true)
                  .catch(showTooltip)
                  .finally(() => removeProcessingLink(link));
              }}
            >
              <ActionText text={i18n.t('open')} />
              <ArrowRight />
            </div>
          )}
        </>
      );
    },
    calculateCellValue: (cellValue) => cellValue.link,
  },
];
